import React from 'react';
import { getValidationThreshold } from '../../config/scoringConfig';

/**
 * Composant de jauge de score de 1 à 10
 * Utilisé dans l'InfoPanel pour afficher le score pondéré
 */
const ScoreGauge = ({ score = 0, maxScore = 10, showLabel = true, size = 'normal' }) => {
    const percentage = Math.min((score / maxScore) * 100, 100);
    const validatedMin = getValidationThreshold();

    const getScoreColor = (s) => {
        if (s >= validatedMin) return '#10b981';
        if (s >= 6) return '#f59e0b';
        if (s >= 4) return '#f97316';
        return '#ef4444';
    };

    const getScoreLabel = (s) => {
        if (s >= validatedMin) return 'Excellent';
        if (s >= 6) return 'Bon';
        if (s >= 4) return 'Moyen';
        return 'Faible';
    };

    const getScoreIcon = (s) => {
        if (s >= validatedMin) {
            // Étoile pour excellent
            return (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            );
        } else if (s >= 6) {
            // Check pour bon
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            );
        } else if (s >= 4) {
            // Triangle pour moyen
            return (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l8 18H4l8-18z"/>
                </svg>
            );
        } else {
            // X pour faible
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        }
    };

    const sizeClasses = {
        small: 'h-2',
        normal: 'h-3',
        large: 'h-4'
    };

    return (
        <div className="bg-gray-700 rounded-lg px-1 py-1 text-center">
            <div className="text-gray-400 text-xs font-bold uppercase mb-1">
                {showLabel ? 'Score' : ''}
            </div>
            
            {/* Jauge de score */}
            <div className={`w-full bg-gray-600 rounded-full ${sizeClasses[size]} mb-2`}>
                <div 
                    className={`rounded-full transition-all duration-500 ease-out ${sizeClasses[size]}`}
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: getScoreColor(score),
                        background: score >= 6 
                            ? `linear-gradient(to right, ${getScoreColor(score)}, ${getScoreColor(Math.min(score + 1, 10))})`
                            : getScoreColor(score)
                    }}
                />
            </div>
            
            {/* Score et label */}
            <div className="flex items-center justify-center gap-2">
                <div className="text-white font-bold text-lg">
                    {score}/{maxScore}
                </div>
                <div 
                    className="text-xs font-semibold flex items-center gap-1"
                    style={{ color: getScoreColor(score) }}
                >
                    {getScoreIcon(score)}
                    {getScoreLabel(score)}
                </div>
            </div>
            
            {/* Pourcentage */}
            <div className="text-gray-400 text-xs mt-1">
                {Math.round(percentage)}%
            </div>
        </div>
    );
};

export default ScoreGauge;
